import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PlayIcon, StopIcon, ArrowPathIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import midiService, { MidiService } from '../services/midiService';
import PerformanceSummary from './PerformanceSummary';
import CustomMidiRoll from './CustomMidiRoll';
import mixpanelService from '../services/mixpanelService';
import apiClient from '../utils/apiClient';
import {
  getLiveClassificationPlan,
  mergeClassifiedHands
} from '../utils/noteClassification';

const responseError = async (response, fallbackMessage) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // The fallback message is still accurate for non-JSON upstream failures.
  }
  const detail = payload?.detail;
  const error = new Error(
    (typeof detail === 'object' && detail?.message) ||
      (typeof detail === 'string' && detail) ||
      fallbackMessage
  );
  error.code = typeof detail === 'object' ? detail?.code : null;
  error.reason = typeof detail === 'object' ? detail?.reason : null;
  return error;
};

const PracticeMode = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [completedNotes, setCompletedNotes] = useState(() => {
    // Try to restore completed notes from sessionStorage on component mount
    const saved = sessionStorage.getItem('practiceMode_completedNotes');
    return saved ? JSON.parse(saved) : [];
  }); // Notes that have ended (note_off received)
  const [activeNotes, setActiveNotes] = useState([]); // Notes currently being held
  const [detectedExercise, setDetectedExercise] = useState(null);
  const [exerciseConfidence, setExerciseConfidence] = useState({ left: 0, right: 0 });
  const [recognitionFailure, setRecognitionFailure] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(() => {
    // Try to restore analysis results from sessionStorage on component mount
    const saved = sessionStorage.getItem('practiceMode_analysisResults');
    return saved ? JSON.parse(saved) : null;
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [pianoRollExpanded, setPianoRollExpanded] = useState(true);
  const [modelConfig, setModelConfig] = useState({ sequence_length: 16 }); // Default to 16, will be loaded from API
  
  // New state for metronome features
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [availableMidiInputs, setAvailableMidiInputs] = useState([]);
  const [selectedMidiInput, setSelectedMidiInput] = useState('');
  
  // State for single-hand detection modal
  const [showHandSelectionModal, setShowHandSelectionModal] = useState(false);
  const [pendingClassifiedNotes, setPendingClassifiedNotes] = useState(null);
  
  // State for forget exercise modal
  const [showForgetExerciseModal, setShowForgetExerciseModal] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  
  // State for CSV demo playback (local dev only)
  const [csvFiles, setCsvFiles] = useState([]);
  const [selectedCsvFile, setSelectedCsvFile] = useState('');
  const [isPlayingCsv, setIsPlayingCsv] = useState(false);
  const [csvPlaybackProgress, setCsvPlaybackProgress] = useState(0);
  const csvPlaybackRef = useRef(null);
  
  // Refs for tracking
  const sessionStartTime = useRef(null);
  const lastNoteStartTime = useRef(null);
  const completedNotesRef = useRef(completedNotes);
  const liveClassificationPromiseRef = useRef(null);
  const lastLiveClassificationCountRef = useRef(0);
  
  // Refs for audio features
  const audioContextRef = useRef(null);
  const metronomeIntervalRef = useRef(null);
  const metronomeAudioRef = useRef(null);

  useEffect(() => {
    completedNotesRef.current = completedNotes;
  }, [completedNotes]);

  const saveCompletedNotes = useCallback(notes => {
    completedNotesRef.current = notes;
    setCompletedNotes(notes);
    sessionStorage.setItem('practiceMode_completedNotes', JSON.stringify(notes));
  }, []);

  const requestNoteClassification = useCallback(async notes => {
    const response = await apiClient.fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/classify-notes`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to classify notes');
    }

    return response.json();
  }, []);

  const requestHandClassification = useCallback(async notes => {
    const response = await apiClient.fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/classify-hands`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to classify hands');
    }

    return response.json();
  }, []);

  const classifyLiveNotes = useCallback(notes => {
    const plan = getLiveClassificationPlan({
      notes,
      lastRequestedCount: lastLiveClassificationCountRef.current,
      sequenceLength: modelConfig.sequence_length
    });
    if (!plan) {
      return;
    }

    lastLiveClassificationCountRef.current = plan.requestedCount;
    const priorClassification = liveClassificationPromiseRef.current ||
      Promise.resolve();
    const classificationPromise = priorClassification
      .catch(() => {
        // A failed note must not prevent later notes from being classified.
      })
      .then(() => requestHandClassification(plan.notes))
      .then(classifiedData => {
        const updated = mergeClassifiedHands(
          completedNotesRef.current,
          classifiedData.notes
        );
        saveCompletedNotes(updated);
      })
      .catch(classificationError => {
        console.warn(
          'PracticeMode: live hand classification deferred:',
          classificationError
        );
      })
      .finally(() => {
        if (liveClassificationPromiseRef.current === classificationPromise) {
          liveClassificationPromiseRef.current = null;
        }
      });

    liveClassificationPromiseRef.current = classificationPromise;
  }, [
    modelConfig.sequence_length,
    requestHandClassification,
    saveCompletedNotes
  ]);

  // Fetch model configuration on mount
  const fetchModelConfig = useCallback(async () => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/model-config?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        cache: 'no-cache'  // Force fresh fetch
      });
      
      if (response.ok) {
        const config = await response.json();
        console.log('PracticeMode: Model config loaded:', config);
        setModelConfig(config);
      } else {
        console.error('PracticeMode: Failed to fetch model config, status:', response.status);
      }
    } catch (error) {
      console.error('PracticeMode: Failed to fetch model config:', error);
      // Keep default value of 16
    }
  }, []);

  useEffect(() => {
    loadConfig();
    checkAndLoadModel();
    fetchModelConfig();
    initializeMidi();
    initializeAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Initialize Web Audio API for metronome only
  const initializeAudio = useCallback(async () => {
    try {
      // Initialize audio context
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        // Audio context created for metronome
      }
      
      // Load metronome sound
      if (!metronomeAudioRef.current) {
        try {
          const response = await fetch('/metronome.wav');
          if (!response.ok) {
            throw new Error(`Failed to fetch metronome.wav: ${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          metronomeAudioRef.current = await audioContextRef.current.decodeAudioData(arrayBuffer);
          // Metronome audio loaded and decoded successfully
        } catch (error) {
          console.error('Failed to load metronome audio:', error);
          // Create a simple beep as fallback
          const sampleRate = audioContextRef.current.sampleRate;
          const duration = 0.1;
          const buffer = audioContextRef.current.createBuffer(1, sampleRate * duration, sampleRate);
          const channelData = buffer.getChannelData(0);
          
          for (let i = 0; i < channelData.length; i++) {
            channelData[i] = Math.sin(2 * Math.PI * 800 * i / sampleRate) * 0.5;
          }
          metronomeAudioRef.current = buffer;
          // Created fallback metronome beep
        }
      }
      
    } catch (error) {
      console.error('Failed to initialize audio for metronome:', error);
    }
  }, []);

  const initializeMidi = async () => {
    try {
      if (MidiService.isSupported()) {
        setStatus('Requesting MIDI access...');
        await midiService.initialize();
        // MIDI service initialized
        
        // Load available MIDI inputs
        const devices = midiService.getInputDevices();
        const connectedDevices = devices.filter(device => device.state === 'connected');
        setAvailableMidiInputs(connectedDevices);
        
        // Set default selection if available
        if (connectedDevices.length > 0 && !selectedMidiInput) {
          setSelectedMidiInput(connectedDevices[0].name);
        }
        
        setStatus('MIDI initialized successfully');
        mixpanelService.track('MIDI Setup Completed', {
          connected_device_count: connectedDevices.length,
          has_device: connectedDevices.length > 0
        });
        setTimeout(() => setStatus(''), 2000);
      } else {
        const browserInfo = MidiService.getBrowserInstructions();
        setError(`Web MIDI API not supported in ${browserInfo.browser}.\n\nTo enable MIDI in ${browserInfo.browser}:\n${browserInfo.instructions.join('\n')}`);
        mixpanelService.track('MIDI Setup Failed', {
          failure_mode: 'unsupported_browser',
          browser: browserInfo.browser
        });
      }
    } catch (err) {
      console.error('Failed to initialize MIDI:', err);
      setError(err.message);
      mixpanelService.trackFailure('midi_setup', err);
      
      // If it's a permission error, provide additional guidance
      if (err.message.includes('denied') || err.message.includes('permission')) {
        setTimeout(() => {
          setError(err.message + '\n\nTo enable MIDI:\n1. Click the 🔒 lock icon in your address bar\n2. Set "MIDI devices" to "Allow"\n3. Refresh this page');
        }, 100);
      }
    }
  };

  const loadConfig = async () => {
    const savedConfig = api.getConfig();
    setConfig(savedConfig);
    
    // If no MIDI port configured, try to get the first available
    if (!savedConfig.midiPort) {
      try {
        const { ports } = await api.fetchMidiPorts();
        if (ports && ports.length > 0) {
          savedConfig.midiPort = ports[0];
          setSelectedMidiInput(ports[0]);
          api.saveConfig(savedConfig);
          setConfig(savedConfig);
        }
      } catch (err) {
        console.error('Failed to fetch MIDI ports:', err);
      }
    } else {
      setSelectedMidiInput(savedConfig.midiPort);
    }
  };

  const checkAndLoadModel = async () => {
    try {
      const status = await api.checkStatus();
      if (!status.model_loaded) {
        const savedConfig = api.getConfig();
        await api.loadModel(savedConfig.model);
      }
    } catch (err) {
      setError('Failed to load model: ' + err.message);
    }
  };

  const refreshMidiPorts = async () => {
    try {
      setStatus('Refreshing MIDI ports...');
      
      // Reinitialize MIDI to get fresh device list
      await midiService.initialize();
      const devices = midiService.getInputDevices();
      const connectedDevices = devices.filter(device => device.state === 'connected');
      setAvailableMidiInputs(connectedDevices);
      
      if (connectedDevices.length > 0) {
        // Keep current selection if still available, otherwise select first
        const currentStillAvailable = connectedDevices.find(device => device.name === selectedMidiInput);
        const newSelection = currentStillAvailable ? selectedMidiInput : connectedDevices[0].name;
        
        setSelectedMidiInput(newSelection);
        const newConfig = { ...config, midiPort: newSelection };
        api.saveConfig(newConfig);
        setConfig(newConfig);
        setStatus('MIDI ports refreshed');
        mixpanelService.track('MIDI Devices Refreshed', {
          connected_device_count: connectedDevices.length
        });
      } else {
        setError('No MIDI devices found');
        setSelectedMidiInput('');
        mixpanelService.track('MIDI Setup Failed', { failure_mode: 'no_devices_found' });
      }
      
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setError('Failed to refresh MIDI ports: ' + err.message);
      mixpanelService.trackFailure('midi_refresh', err);
    }
  };
  
  const handleMidiInputChange = (newInput) => {
    setSelectedMidiInput(newInput);
    const newConfig = { ...config, midiPort: newInput };
    api.saveConfig(newConfig);
    setConfig(newConfig);
    mixpanelService.track('MIDI Device Selected');
  };
  
  
  // Ensure we have a valid audio context
  const ensureAudioContext = useCallback(async () => {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      // Creating new audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // Reload metronome audio for new context
      metronomeAudioRef.current = null;
      await initializeAudio();
    }
    
    if (audioContextRef.current.state === 'suspended') {
      // Resuming audio context
      await audioContextRef.current.resume();
    }
    
    return audioContextRef.current;
  }, [initializeAudio]);
  
  // Play metronome click
  const playMetronomeClick = useCallback(async () => {
    try {
      const context = await ensureAudioContext();
      
      if (!metronomeAudioRef.current) {
        return;
      }
      
      const source = context.createBufferSource();
      source.buffer = metronomeAudioRef.current;
      source.connect(context.destination);
      source.start();
      // Metronome click played
    } catch (error) {
      console.error('Error playing metronome click:', error);
    }
  }, [ensureAudioContext]);
  
  // Start/stop metronome
  const toggleMetronome = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
    
    if (metronomeEnabled && isRecording) {
      const intervalMs = (60 / bpm) * 1000;
      // Starting metronome
      
      // Play first click immediately
      playMetronomeClick();
      
      // Set up interval for subsequent clicks
      metronomeIntervalRef.current = setInterval(() => {
        playMetronomeClick();
      }, intervalMs);
    }
  }, [metronomeEnabled, isRecording, bpm, playMetronomeClick]);
  
  useEffect(() => {
    toggleMetronome();
  }, [isRecording, metronomeEnabled, bpm, toggleMetronome]);
  

  const startRecording = async () => {
    mixpanelService.track('Practice Session Start Requested', {
      metronome_enabled: metronomeEnabled,
      bpm
    });

    if (!selectedMidiInput) {
      setError('No MIDI device selected. Please select a MIDI input.');
      mixpanelService.track('Practice Session Failed', {
        stage: 'start',
        failure_mode: 'no_midi_device'
      });
      return;
    }

    try {
      // Initialize audio context - this is a user interaction so we can start audio
      await ensureAudioContext();
      
      setError('');
      setStatus('Starting practice session...');
      setCompletedNotes([]);
      completedNotesRef.current = [];
      setActiveNotes([]);
      setDetectedExercise(null);
      setAnalysisResults(null);
      setRecognitionFailure(null);
      setPianoRollExpanded(true);
      lastLiveClassificationCountRef.current = 0;
      liveClassificationPromiseRef.current = null;
      
      // Clear any previously stored data when starting a new recording
      sessionStorage.removeItem('practiceMode_analysisResults');
      sessionStorage.removeItem('practiceMode_completedNotes');
      
      // Set session start time
      sessionStartTime.current = Date.now();
      lastNoteStartTime.current = null;
      
      // Find the MIDI device to connect to
      const devices = midiService.getInputDevices();
      // First try to find a connected device, then fall back to any device with matching name
      let targetDevice = devices.find(device => device.name === selectedMidiInput && device.state === 'connected');
      if (!targetDevice) {
        targetDevice = devices.find(device => device.name === selectedMidiInput);
      }
      
      if (!targetDevice) {
        setError('MIDI device not found or disconnected. Please refresh MIDI ports.');
        mixpanelService.track('Practice Session Failed', {
          stage: 'start',
          failure_mode: 'midi_device_disconnected'
        });
        return;
      }
      
      // Remove any existing listener before adding a new one
      if (window._currentMidiListener) {
        midiService.removeEventListener(window._currentMidiListener);
        window._currentMidiListener = null;
      }
      
      // Connect to MIDI device and start recording
      midiService.connectToDevice(targetDevice.id);
      midiService.clearNoteBuffer();
      
      // MIDI connected and recording started
      
      // Add MIDI listener for new flow
      const midiListener = (eventType, data) => {
        if (eventType === 'note') {
          if (data.event_type === 'note_on') {

            // Note ON: Add to active notes and log in viewer
            const currentTime = Date.now();
            const sessionTime = (currentTime - sessionStartTime.current) / 1000; // seconds since session start
            
            const activeNote = {
              note: data.note,
              velocity: data.velocity,
              timestamp: sessionTime,
              sourceTimestamp: data.timestamp,
              startTime: currentTime,
              channel: data.channel || 0,
              hand: null // Will be classified later
            };
            
            // Note ON event processed
            
            setActiveNotes(prev => [...prev, activeNote]);
            
            // Update last note start time for relative timing
            lastNoteStartTime.current = sessionTime;
            
          } else if (data.event_type === 'note_off') {
            // Note OFF: Move from active to completed notes
            setActiveNotes(prev => {
              const activeIndex = prev.findIndex(n => n.note === data.note);
              if (activeIndex === -1) {
                // No matching active note found
                return prev;
              }
              
              const activeNote = prev[activeIndex];
              const currentTime = Date.now();
              const duration = currentTime - activeNote.startTime; // duration in milliseconds
              
              const completedNote = {
                ...activeNote,
                duration: duration,
                endTime: currentTime,
                // Preserve any classification that was already applied to the active note
                hand: activeNote.hand || null,
                confidence: activeNote.confidence || null
              };
              
              // Note completed and moved to completed notes
              
              // Keep a local source of truth so simultaneous note-off events do
              // not race React state updates. Hand inference runs in bounded
              // model-sized batches and never allows overlapping requests.
              const newCompleted = [
                ...completedNotesRef.current,
                completedNote
              ];
              saveCompletedNotes(newCompleted);
              classifyLiveNotes(newCompleted);
              
              
              // Remove from active notes
              return prev.filter((_, index) => index !== activeIndex);
            });
          }
        }
      };
      
      midiService.addEventListener(midiListener);
      window._currentMidiListener = midiListener;
      
      setIsRecording(true);
      setStatus('Recording... Play some notes on your MIDI device');
      mixpanelService.trackPracticeStart({
        metronome_enabled: metronomeEnabled,
        bpm
      });
    } catch (err) {
      setError(err.message);
      setStatus('');
      mixpanelService.trackFailure('practice_session_start', err);
    }
  };

  // Handle user's hand selection for single-hand recordings
  const handleHandSelection = async (selectedHand) => {
    setShowHandSelectionModal(false);
    
    if (!pendingClassifiedNotes) return;
    
    try {
      setStatus('Analyzing performance...');
      
      // Update all single-hand notes to the selected hand
      const updatedNotes = pendingClassifiedNotes.notes.map(note => ({
        ...note,
        hand: note.hand === 'single-hand' ? selectedHand : note.hand
      }));
      saveCompletedNotes(
        mergeClassifiedHands(completedNotesRef.current, updatedNotes)
      );
      
      // Format notes with all required information
      const formattedNotes = updatedNotes.map(note => ({
        note: note.note,
        velocity: note.velocity,
        timestamp: note.timestamp,
        duration: note.duration || 0.1,
        hand: note.hand || 'unknown',
        channel: note.channel || 0,
        finger: note.finger || null
      }));
      
      // Get recorded notes for metadata
      const recordedNotes = midiService.formatNotesForBackend();
      
      // Store the exercise session for analysis
      const sessionResponse = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/store-exercise`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: formattedNotes,
          exercise_name: pendingClassifiedNotes.exercise_detected || 'unknown',
          start_timestamp: new Date(Date.now() - (recordedNotes.length > 0 ? (recordedNotes[recordedNotes.length - 1].timestamp - recordedNotes[0].timestamp) * 1000 : 0)),
          end_timestamp: new Date(),
          metronome_enabled: metronomeEnabled,
          bpm: bpm
        })
      });
      
      if (!sessionResponse.ok) {
        throw await responseError(
          sessionResponse,
          'Failed to store exercise session'
        );
      }
      
      const analysisResults = await sessionResponse.json();
      setAnalysisResults(analysisResults);
      sessionStorage.setItem('practiceMode_analysisResults', JSON.stringify(analysisResults));
      
      setStatus('Analysis complete!');
      setPendingClassifiedNotes(null);
      
      mixpanelService.trackPracticeCompleted({
        notes_count: formattedNotes.length,
        exercise_name: pendingClassifiedNotes.exercise_detected || 'unknown',
        duration_seconds: recordedNotes.length > 1
          ? recordedNotes[recordedNotes.length - 1].timestamp - recordedNotes[0].timestamp
          : 0,
        selected_hand: selectedHand,
        single_hand: true,
        metronome_enabled: metronomeEnabled,
        bpm
      });
      
    } catch (error) {
      console.error('Analysis after hand selection failed:', error);
      setError(`Analysis failed: ${error.message}`);
      setAnalysisResults(null);
      if (error.code === 'exercise_not_recognized') {
        setRecognitionFailure({
          reason: error.reason,
          message:
            'This hand selection did not confidently match a supported exercise.'
        });
      } else {
        setRecognitionFailure({
          reason: 'analysis_unavailable',
          message:
            'We could not produce a trustworthy analysis, so this recording was not saved.'
        });
      }
      setStatus('');
      mixpanelService.trackFailure('practice_session_analysis', error, {
        stage: 'hand_selection'
      });
    }
  };

  // Handle forget exercise confirmation
  const handleForgetExercise = async () => {
    if (!analysisResults || !analysisResults.session_id) {
      setError('No session to delete');
      return;
    }

    setIsDeletingSession(true);
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/sessions/${analysisResults.session_id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete session');
      }

      mixpanelService.track('Exercise Deleted', {
        session_id: analysisResults.session_id,
        exercise_name: analysisResults.exercise_name
      });

      // Reset to clean recording state
      setAnalysisResults(null);
      setCompletedNotes([]);
      setActiveNotes([]);
      setDetectedExercise(null);
      setExerciseConfidence({ left: 0, right: 0 });
      sessionStorage.removeItem('practiceMode_analysisResults');
      sessionStorage.removeItem('practiceMode_completedNotes');
      
      // Expand piano roll to show recording interface
      setPianoRollExpanded(true);
      
      setShowForgetExerciseModal(false);
      setStatus('Exercise deleted successfully');
      setTimeout(() => setStatus(''), 3000);

    } catch (error) {
      console.error('Failed to delete exercise:', error);
      setError(`Failed to delete exercise: ${error.message}`);
      mixpanelService.trackFailure('exercise_delete', error);
    } finally {
      setIsDeletingSession(false);
    }
  };
  
  const stopRecording = async () => {
    try {
      setStatus('Analyzing performance...');
      
      // Remove the MIDI listener first
      if (window._currentMidiListener) {
        midiService.removeEventListener(window._currentMidiListener);
        window._currentMidiListener = null;
      }
      
      // Stop MIDI recording (but keep the buffer!)
      midiService.disconnect();
      
      // No WebSocket cleanup needed anymore
      
      // Get all recorded notes from MIDI service (already filtered to note_on events)
      const recordedNotes = midiService.formatNotesForBackend();

      // Let the one bounded live request finish before issuing the final
      // full-performance classification. This prevents overlapping inference.
      if (liveClassificationPromiseRef.current) {
        await liveClassificationPromiseRef.current;
      }
      
      // Stop recording: notes captured and ready for analysis
      
      const recognitionMinimum = modelConfig.recognition_min_notes || 10;
      if (recordedNotes.length < recognitionMinimum) {
        setError(`Not enough notes recorded for analysis. Please record at least ${recognitionMinimum} notes.`);
        setIsRecording(false);
        setStatus('');
        mixpanelService.track('Practice Session Failed', {
          stage: 'analysis',
          failure_mode: 'insufficient_notes',
          notes_count: recordedNotes.length
        });
        return;
      }
      
      // Send notes to backend for classification and analysis
      try {
        // Step 1: Classify notes with hand labels
        const classifiedData = await requestNoteClassification(recordedNotes);
        saveCompletedNotes(
          mergeClassifiedHands(
            completedNotesRef.current,
            classifiedData.notes
          )
        );
        // Notes classified successfully

        if (
          classifiedData.recognition_status !== 'recognized' ||
          !classifiedData.exercise_detected
        ) {
          const reasonMessages = {
            insufficient_notes: 'Play a little more of the exercise and try again.',
            insufficient_notes_per_hand: 'Play a little more with each active hand and try again.',
            below_similarity_threshold: 'The recording did not closely match a supported exercise.',
            ambiguous_match: 'The recording was too close to multiple supported exercises.',
            canonical_pitch_mismatch: 'The played notes did not agree closely enough with the expected exercise.',
            hand_disagreement: 'The two hands appeared to be playing different exercises.',
            recognition_unavailable: 'Exercise recognition is temporarily unavailable.'
          };
          setRecognitionFailure({
            reason: classifiedData.recognition_reason,
            message:
              reasonMessages[classifiedData.recognition_reason] ||
              'This recording could not be matched confidently to a supported exercise.'
          });
          setAnalysisResults(null);
          sessionStorage.removeItem('practiceMode_analysisResults');
          setStatus('');
          setError('');
          setIsRecording(false);
          setPianoRollExpanded(false);
          mixpanelService.track('Exercise Not Recognized', {
            reason: classifiedData.recognition_reason || 'unknown',
            confidence: classifiedData.confidence ?? null,
            margin: classifiedData.recognition_margin ?? null,
            pitch_agreement: classifiedData.pitch_agreement ?? null,
            notes_count: recordedNotes.length
          });
          return;
        }

        // Check if majority of notes are classified as 'single-hand'
        const singleHandCount = classifiedData.notes.filter(n => n.hand === 'single-hand').length;
        const totalNotes = classifiedData.notes.length;
        
        if (singleHandCount > totalNotes * 0.5) {
          // Majority are single-hand - show modal and pause processing
          setPendingClassifiedNotes(classifiedData);
          setShowHandSelectionModal(true);
          setStatus('Please indicate which hand you used');
          setIsRecording(false);
          return;
        }

        // Step 2: Format notes with all required information
        const formattedNotes = classifiedData.notes.map(note => ({
          note: note.note,  // Backend uses 'note' as field name, not 'note_number'
          velocity: note.velocity,
          timestamp: note.timestamp,
          duration: note.duration || 0.1,
          hand: note.hand || 'unknown',
          channel: note.channel || 0,
          finger: note.finger || null
        }));
        
        // Store the exercise session for analysis
        const sessionResponse = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/store-exercise`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            notes: formattedNotes,
            exercise_name: classifiedData.exercise_detected || 'unknown',
            start_timestamp: new Date(Date.now() - (recordedNotes.length > 0 ? (recordedNotes[recordedNotes.length - 1].timestamp - recordedNotes[0].timestamp) * 1000 : 0)),
            end_timestamp: new Date(),
            metronome_enabled: metronomeEnabled,
            bpm: bpm
          })
        });

        if (!sessionResponse.ok) {
          throw await responseError(
            sessionResponse,
            'Failed to store exercise session'
          );
        }

        const analysisResults = await sessionResponse.json();
        // Analysis results received
        
        // Store the analysis results
        setAnalysisResults(analysisResults);
        setRecognitionFailure(null);
        
        // Persist analysis results to sessionStorage so they survive navigation
        sessionStorage.setItem('practiceMode_analysisResults', JSON.stringify(analysisResults));
        mixpanelService.trackPracticeCompleted({
          notes_count: formattedNotes.length,
          exercise_name: classifiedData.exercise_detected || 'unknown',
          duration_seconds: recordedNotes.length > 1
            ? recordedNotes[recordedNotes.length - 1].timestamp - recordedNotes[0].timestamp
            : 0,
          single_hand: false,
          metronome_enabled: metronomeEnabled,
          bpm,
          accuracy_score: analysisResults.accuracy_score ?? null
        });

      } catch (error) {
        console.error('Backend analysis failed:', error);
        setError(`Analysis failed: ${error.message}`);
        setAnalysisResults(null);
        setRecognitionFailure({
          reason: 'analysis_unavailable',
          message:
            'We could not produce a trustworthy analysis, so this recording was not saved.'
        });
        mixpanelService.trackFailure('practice_session_analysis', error, {
          notes_count: recordedNotes.length,
          metronome_enabled: metronomeEnabled,
          bpm
        });
      }
      setPianoRollExpanded(false); // Collapse piano roll when showing results
      
      setIsRecording(false);
    } catch (err) {
      setError(err.message);
      setStatus('');
      setIsRecording(false);
      mixpanelService.trackFailure('practice_session_stop', err);
    }
  };

  // CSV Demo Playback Functions (local dev only)
  const isDevelopment = () => {
    return process.env.NODE_ENV === 'development' || 
           window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1';
  };

  const fetchCsvFiles = async () => {
    if (!isDevelopment()) return;
    
    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/csv-files`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsvFiles(data.files || []);
        if (data.files && data.files.length > 0) {
          setSelectedCsvFile(data.files[0].name);
        }
      }
    } catch (error) {
      console.error('Failed to fetch CSV files:', error);
    }
  };

  const startCsvPlayback = async () => {
    if (!selectedCsvFile || isPlayingCsv || isRecording) return;
    
    try {
      setIsPlayingCsv(true);
      setCsvPlaybackProgress(0);
      setError('');
      setStatus('Loading demo recording...');
      
      // Fetch CSV content
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/admin/csv-file/${selectedCsvFile}`,
        {
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to load CSV file');
      }
      
      const data = await response.json();
      const notes = data.notes;
      
      setStatus(`Playing demo: ${selectedCsvFile} (${notes.length} notes)`);
      
      // Clear previous session data
      setCompletedNotes([]);
      setActiveNotes([]);
      setDetectedExercise(null);
      setAnalysisResults(null);
      setRecognitionFailure(null);
      sessionStorage.removeItem('practiceMode_completedNotes');
      sessionStorage.removeItem('practiceMode_analysisResults');
      
      // Start session timing
      sessionStartTime.current = Date.now();
      lastNoteStartTime.current = 0;
      
      // Set up MIDI listener for CSV playback
      const midiListener = (eventType, data) => {
        if (eventType === 'note') {
          if (data.event_type === 'note_on') {
            const currentTime = Date.now();
            const sessionTime = (currentTime - sessionStartTime.current) / 1000;
            
            const activeNote = {
              note: data.note,
              velocity: data.velocity,
              timestamp: sessionTime,
              startTime: currentTime,
              channel: data.channel || 0,
              hand: data.hand || null
            };
            
            setActiveNotes(prev => [...prev, activeNote]);
            lastNoteStartTime.current = sessionTime;
            
          } else if (data.event_type === 'note_off') {
            setActiveNotes(prev => {
              const activeNote = prev.find(n => n.note === data.note);
              if (activeNote) {
                const currentTime = Date.now();
                const duration = (currentTime - activeNote.startTime) / 1000;
                
                const completedNote = {
                  ...activeNote,
                  duration: duration,
                  hand: data.hand || activeNote.hand
                };
                
                setCompletedNotes(prevCompleted => {
                  const updated = [...prevCompleted, completedNote];
                  sessionStorage.setItem('practiceMode_completedNotes', JSON.stringify(updated));
                  return updated;
                });
                
                return prev.filter(n => n.note !== data.note);
              }
              return prev;
            });
          }
        }
      };
      
      midiService.addEventListener(midiListener);
      
      // Start playback
      csvPlaybackRef.current = midiService.playbackCSVNotes(
        notes,
        (progress) => {
          setCsvPlaybackProgress(progress);
        },
        () => {
          // Playback complete
          midiService.removeEventListener(midiListener);
          setStatus('Demo playback complete. Analyzing...');
          setIsPlayingCsv(false);
          setCsvPlaybackProgress(1);
          
          // Automatically stop and analyze
          setTimeout(() => {
            stopRecording();
          }, 500);
        }
      );
      
    } catch (error) {
      console.error('CSV playback error:', error);
      setError(`Demo playback failed: ${error.message}`);
      setIsPlayingCsv(false);
      setStatus('');
    }
  };

  const stopCsvPlayback = () => {
    if (csvPlaybackRef.current) {
      csvPlaybackRef.current.stop();
      csvPlaybackRef.current = null;
    }
    setIsPlayingCsv(false);
    setCsvPlaybackProgress(0);
    setStatus('Demo playback stopped');
    
    // Analyze what we have so far
    if (completedNotes.length > 0) {
      setTimeout(() => {
        stopRecording();
      }, 100);
    }
  };

  // Load CSV files on mount (dev only)
  useEffect(() => {
    if (isDevelopment()) {
      fetchCsvFiles();
    }
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      // Clear metronome interval
      if (metronomeIntervalRef.current) {
        clearInterval(metronomeIntervalRef.current);
      }
      
      // Stop CSV playback if active
      if (csvPlaybackRef.current) {
        csvPlaybackRef.current.stop();
      }
      
      // Close audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="min-h-full flex flex-col p-4 space-y-4">
      
      {/* Header with controls - fixed height */}
      <div className="card p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {!isRecording ? (
              <button
                onClick={startRecording}
                className="btn-primary flex items-center space-x-2"
                disabled={!selectedMidiInput}
              >
                <PlayIcon className="h-5 w-5" />
                <span>Start Recording</span>
              </button>
            ) : (
              <button
                onClick={stopRecording}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <StopIcon className="h-5 w-5" />
                <span>Stop Recording</span>
              </button>
            )}
            
            {/* Metronome controls in box */}
            <div className="px-4 py-2 border border-gray-300 rounded-lg bg-white flex items-center space-x-3 min-w-fit">
              <input
                type="checkbox"
                id="metronome"
                checked={metronomeEnabled}
                onChange={(e) => setMetronomeEnabled(e.target.checked)}
                disabled={isRecording}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="metronome" className="text-sm font-medium text-gray-700 whitespace-nowrap">Metronome</label>
              <input
                type="range"
                min="40"
                max="240"
                value={bpm}
                onChange={(e) => setBpm(parseInt(e.target.value))}
                disabled={isRecording}
                className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${((bpm - 40) / (240 - 40)) * 100}%, #e5e7eb ${((bpm - 40) / (240 - 40)) * 100}%, #e5e7eb 100%)`
                }}
              />
              <span className="text-sm font-medium text-gray-700 w-14 whitespace-nowrap">{bpm} BPM</span>
            </div>
            
            
            
            {error && error.includes('denied') && (
              <button
                onClick={initializeMidi}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <span>🎹</span>
                <span>Request MIDI Access</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            {/* MIDI input controls - moved to right side */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">MIDI:</span>
              <select
                value={selectedMidiInput}
                onChange={(e) => handleMidiInputChange(e.target.value)}
                disabled={isRecording}
                className="text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 min-w-48"
              >
                {availableMidiInputs.length === 0 ? (
                  <option value="">No MIDI devices found</option>
                ) : (
                  availableMidiInputs.map((device) => (
                    <option key={device.id} value={device.name}>
                      {device.name}
                    </option>
                  ))
                )}
              </select>
              <button
                onClick={refreshMidiPorts}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                disabled={isRecording}
                title="Refresh MIDI devices"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            {isRecording && (
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-red-600">Recording</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Status and error messages */}
        {status && (
          <div className="mt-3 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
            {status}
          </div>
        )}
        
        {error && (
          <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <div className="whitespace-pre-line">{error}</div>
            {error.includes('denied') && (
              <div className="mt-2 text-xs text-red-600">
                💡 <strong>Quick Fix:</strong> Look for a MIDI permission popup in your browser, or click the lock icon (🔒) in your address bar to allow MIDI access.
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Demo Playback Controls (Development/Local Only) */}
      {isDevelopment() && csvFiles.length > 0 && (
        <div className="card p-4 flex-shrink-0 border-2 border-yellow-300 bg-yellow-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-yellow-800">🎬 Demo Mode</span>
              <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full font-medium">
                LOCAL ONLY
              </span>
            </div>
            <span className="text-xs text-yellow-700">
              Playback CSV recordings as if playing live
            </span>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 flex-1">
              <label className="text-sm font-medium text-yellow-900 whitespace-nowrap">
                Recording:
              </label>
              <select
                value={selectedCsvFile}
                onChange={(e) => setSelectedCsvFile(e.target.value)}
                disabled={isPlayingCsv || isRecording}
                className="flex-1 text-sm border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500 bg-white"
              >
                {csvFiles.map((file) => (
                  <option key={file.name} value={file.name}>
                    {file.name} ({file.note_count} notes)
                  </option>
                ))}
              </select>
            </div>
            
            {!isPlayingCsv ? (
              <button
                onClick={startCsvPlayback}
                disabled={!selectedCsvFile || isRecording}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <PlayIcon className="h-5 w-5" />
                <span>Play Demo</span>
              </button>
            ) : (
              <button
                onClick={stopCsvPlayback}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <StopIcon className="h-5 w-5" />
                <span>Stop Demo</span>
              </button>
            )}
          </div>
          
          {/* Playback progress bar */}
          {isPlayingCsv && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-yellow-700 mb-1">
                <span>Playback Progress</span>
                <span>{Math.round(csvPlaybackProgress * 100)}%</span>
              </div>
              <div className="w-full bg-yellow-200 rounded-full h-2">
                <div
                  className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${csvPlaybackProgress * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Exercise detection display - fixed height when visible */}
      {isRecording && detectedExercise && (
        <div className="card p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 mb-1">Detected Exercise</div>
              <div className="text-2xl font-bold text-indigo-600">
                {detectedExercise.replace(/_/g, ' ').toUpperCase()}
              </div>
            </div>
            <div className="flex space-x-3">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Left Hand</div>
                <div className={`text-lg font-semibold ${exerciseConfidence.left > 0.7 ? 'text-green-600' : 'text-gray-400'}`}>
                  {(exerciseConfidence.left * 100).toFixed(0)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Right Hand</div>
                <div className={`text-lg font-semibold ${exerciseConfidence.right > 0.7 ? 'text-green-600' : 'text-gray-400'}`}>
                  {(exerciseConfidence.right * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content area - flexible height */}
      <div className="flex-1 flex flex-col">
        {/* MIDI Roll - consistent height whether recording or not */}
        <div className="card flex-shrink-0">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Live MIDI Input</h3>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500">
                  {completedNotes.length} completed, {activeNotes.length} active
                </span>
                {!isRecording && (
                  <button
                    onClick={() => setPianoRollExpanded(!pianoRollExpanded)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    {pianoRollExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* MIDI Roll content - always same height, always visible during recording */}
          {(isRecording || pianoRollExpanded) && (
            <div className="border-t border-gray-200">
              <div className="p-4 h-96">
                <CustomMidiRoll 
                  notes={completedNotes} 
                  activeNotes={activeNotes} 
                  isRecording={isRecording}
                  analysisResults={analysisResults}
                />
              </div>
            </div>
          )}
        </div>

        {/* Buffer zone */}
        <div className="h-4 flex-shrink-0"></div>

        {/* Performance Summary - visible after recording */}
        {!isRecording && analysisResults && (
          <div className="flex-1 overflow-auto">
            <PerformanceSummary analysisResults={analysisResults} />
            
            {/* Forget Exercise Button */}
            {analysisResults.session_id && (
              <div className="mt-4 flex justify-center pb-4">
                <button
                  onClick={() => setShowForgetExerciseModal(true)}
                  className="px-6 py-2 bg-red-50 text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors duration-200 font-medium text-sm"
                >
                  Forget Exercise
                </button>
              </div>
            )}
          </div>
        )}

        {!isRecording && recognitionFailure && (
          <div className="flex-1 overflow-auto">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {recognitionFailure.reason === 'analysis_unavailable'
                  ? 'Analysis unavailable'
                  : 'Exercise not recognized'}
              </h2>
              <p className="mt-3 text-gray-600">{recognitionFailure.message}</p>
              <p className="mt-2 text-sm text-gray-500">
                No exercise-specific analysis was generated and no practice session was saved.
              </p>
              <button
                onClick={startRecording}
                disabled={!selectedMidiInput}
                className="mt-6 btn-primary"
              >
                Try another recording
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Hand Selection Modal */}
      {showHandSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Which hand did you use?</h2>
            <p className="text-gray-600 mb-6">
              The recording appears to be single-handed. Please indicate which hand you used.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleHandSelection('left')}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
              >
                Left Hand
              </button>
              <button
                onClick={() => handleHandSelection('right')}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
              >
                Right Hand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forget Exercise Confirmation Modal */}
      {showForgetExerciseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Forget this exercise?</h2>
            <p className="text-gray-600 mb-6">
              This will permanently delete this exercise recording and all its data from your practice history. This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowForgetExerciseModal(false)}
                disabled={isDeletingSession}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleForgetExercise}
                disabled={isDeletingSession}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingSession ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeMode;
